import type { JsonObject, JsonValue } from '../app/json/jsonValue.js';
import type { PreparedContent } from './preparedContent.js';

/**
 * Declares a value to capture from a scenario's result after it runs, for later substitution.
 * Source form: "<scheme>:<arg>" resolved by a registered IDataSource (e.g. "actionresult:UserId").
 */
export class CaptureDecl {
    Name: string;
    Source: string;

    constructor(name: string, source: string) {
        this.Name = name;
        this.Source = source;
    }
}

/**
 * Declares a substitution applied to a scenario's config before it runs: when configJson[field] equals
 * sentinel, replace it with the previously captured value named "from".
 */
export class SubstitutionDecl {
    Field: string;
    Sentinel: string;
    From: string;

    constructor(field: string, sentinel: string, from: string) {
        this.Field = field;
        this.Sentinel = sentinel;
        this.From = from;
    }
}

/**
 * Declares a generic state-transition assertion evaluated by the harness at a step boundary.
 * Subsystem-agnostic: every check reduces to "produce a JSON document, select a node (RFC-6901 JSON
 * Pointer), assert a predicate, at a phase". Mirrors csharp StateValidationDecl.
 *   when   - "before" | "after" the step (default "after").
 *   source - "<scheme>:<arg>": "appdata:<relpath>" (persisted App_Data json file),
 *            "inmemory:<hookName>" (registered snapshot provider), "actionresult[:<key>]".
 *   select - RFC-6901 JSON Pointer into the source document ("" = whole document).
 *   assert - exists | absent | equals | count==N | count>=N | count<=N | count!=N | contains | not_contains.
 *   value  - native predicate argument (scalar for equals/scalar-contains; object subset for
 *            contains/not_contains — only named keys compared, so volatile GUIDs/timestamps are ignored).
 *   label  - human-readable description used in pass/fail reporting.
 */
export class StateValidationDecl {
    static readonly phaseBefore = 'before';
    static readonly phaseAfter = 'after';

    // The step boundary this assertion evaluates at: "before" or "after" the step's merge runs.
    // (JSON key "phase".)
    phase: string;
    source: string;
    select: string;
    assertPredicate: string;
    value: JsonValue | null;
    label: string;

    constructor(phase: string, source: string, select: string, assert: string, value: JsonValue | null, label: string) {
        this.phase = (phase && phase.trim() !== '') ? phase.trim().toLowerCase() : StateValidationDecl.phaseAfter;
        this.source = source || '';
        this.select = select || '';
        this.assertPredicate = assert || '';
        this.value = value;
        this.label = label || '';
    }

    isBefore(): boolean { return this.phase === StateValidationDecl.phaseBefore; }
}

export class Scenario {
    static readonly appDataResetNone = 'None';
    static readonly appDataResetBefore = 'Before';
    static readonly appDataResetAfter = 'After';
    static readonly appDataResetBeforeAfter = 'BeforeAfter';

    // Declared execution tier for a RequestJson scenario (from the "tier" property). "Server" (default) —
    // the client re-merge lane is scored byte-equal to the server render. "Client" — the scenario asserts a
    // CLIENT-tier outcome that is NOT server-comparable by construction (e.g. a mutating counter whose client
    // re-merge legitimately diverges from the server's persisted value); such a row is scored on a client-vs-
    // client verdict instead of client-vs-server. See PLAN_HANDLER_FIX §16.
    static readonly tierServer = 'Server';
    static readonly tierClient = 'Client';

    appSite: string;
    appView: string;
    sourceFolder: string;
    configName: string;
    private _configJson: JsonObject | null;
    refreshComponent: string;
    order: number;  // Execution order for sequential testing (0 = unordered/standard, >0 = sequential)
    appDataReset: string;
    batchNo: number;  // Global batch order (ascending); same number = same batch. Default 0. (Parity with csharp; node orders by global `order` which is monotonic with batchNo.)
    batch: string;  // Human-readable batch label. Defaults to appSite when absent.
    isPrefixOnly: boolean;  // Transient: prerequisite step pulled in by single-config prefix-replay (silent setup).
    captures: CaptureDecl[] | null;  // Declared captures (from RequestJson "captures"); resolved after the scenario runs via a registered IDataSource.
    substitutions: SubstitutionDecl[] | null;  // Declared substitutions (from RequestJson "substitutions"); applied to configJson before the scenario runs.
    expectedOutcome: string;  // Optional declared outcome ("Rendered"/"Redirected"/"Rejected", from RequestJson "expectedOutcome"); empty = no assertion. Asserted by the §B ordered-sequence classifier.
    stateValidations: StateValidationDecl[] | null;  // Declared generic state-transition assertions (from RequestJson "stateValidations"); evaluated before/after the step by the harness StateValidator.
    appDataSeed: JsonObject | null;  // Declared per-scenario App_Data seed (from RequestJson "appDataSeed"), an object keyed by store name (e.g. "OtpData"). Layered ON TOP of the baseline restore, so a scenario can start from genuine real-aged records (expired OTP / aged cooldown) without a test-only clock seam. Applied by the App_Data reset chain during this scenario's appDataReset. null = pure baseline.
    requiresFeature: string;  // Optional harness feature requirement; unmet scenarios are excluded before execution.
    tier: string;  // Declared execution tier ("Server" default / "Client", from RequestJson "tier"). A Client-tier scenario is scored on a client-vs-client verdict, not client-vs-server byte equality. See PLAN_HANDLER_FIX §16.

    // Handler-presence flags for this scenario's AppSite (stamped by ConfigFile.buildScenariosFromMetadata from
    // the resolved dynamic config). Server-side handlers (Data/Action/AppSite) run only on ConfigFile and are NOT
    // reproducible by the reconstruction sources (ConfigClient/ConfigOCI) — the harness filters those scenarios
    // out of the client re-merge axes. View, ClientData and Client handlers are all client-reproducible.
    // AppSite reflects folder/appsite-level dynamicappsite only (root-level redirects are HTTP-pipeline, not merge).
    //
    // Together these six form the handler-kind profile the test report renders as its Hdlr column; a scenario
    // with none of them set is a PURE-STATIC row, which is itself a reportable kind (an all-green client axis on
    // a static row proves nothing about handler reproduction). See PLAN_HANDLER_FIX §14.
    haveViewHandler: boolean;
    haveServerDataHandler: boolean;
    haveServerActionHandler: boolean;
    haveServerAppSiteHandler: boolean;
    // CLIENT-DATA preparer (IClientData) bound in dynamicclientdata.json. Its presence is what makes an AppSite's
    // server Data/Action handlers client-reproducible, so it is the un-blocker the serverOnly rule already reads.
    haveClientDataHandler: boolean;
    // CLIENT consumer (IClient) declared by a data-client-handler attribute on one of the AppSite's templates.
    // Distinct from haveClientDataHandler: the preparer produces the raw island server-side, the consumer filters
    // it client-side. The loader refuses to run a consumer that its AppSite does not also bind in
    // dynamicclientdata.json, so in practice this implies haveClientDataHandler — but they are separate facts and
    // are stamped separately rather than one standing in for the other.
    haveClientHandler: boolean;

    // readme.txt-derived display metadata for this scenario's AppSite (stamped by
    // ConfigFile.buildScenariosFromMetadata from the index, which carries the readme content). readmeTitle is
    // the readme's line 2, readmeDescription its line 3. Stamped here rather than read by a handler so no data
    // handler performs file IO: ScenariosFilter composes the final displayName/description from these purely.
    // Empty means "no readme (line)" — the two cases are deliberately indistinguishable to consumers.
    readmeTitle: string;
    readmeDescription: string;

    constructor(appSite: string, appView: string, sourceFolder: string = '', order: number = 0, appDataReset: string = Scenario.appDataResetNone) {
        this.appSite = appSite;
        this.appView = appView;
        this.sourceFolder = sourceFolder;
        this.configName = '';
        this._configJson = null;
        this.refreshComponent = '';
        this.order = order;
        this.appDataReset = Scenario.normalizeAppDataReset(appDataReset);
        this.batchNo = 0;
        this.batch = '';
        this.isPrefixOnly = false;
        this.captures = null;
        this.substitutions = null;
        this.expectedOutcome = '';
        this.stateValidations = null;
        this.appDataSeed = null;
        this.requiresFeature = '';
        this.tier = Scenario.tierServer;
        this.haveViewHandler = false;
        this.haveServerDataHandler = false;
        this.haveServerActionHandler = false;
        this.haveServerAppSiteHandler = false;
        this.haveClientDataHandler = false;
        this.haveClientHandler = false;
        this.readmeTitle = '';
        this.readmeDescription = '';
    }

    get configJson(): JsonObject | null { return this._configJson; }
    set configJson(value: JsonObject | null) { this._configJson = value; }

    requiresAppDataResetBefore(): boolean {
        const normalizedReset = Scenario.normalizeAppDataReset(this.appDataReset);
        return normalizedReset === Scenario.appDataResetBefore
            || normalizedReset === Scenario.appDataResetBeforeAfter;
    }

    requiresAppDataResetAfter(): boolean {
        const normalizedReset = Scenario.normalizeAppDataReset(this.appDataReset);
        return normalizedReset === Scenario.appDataResetAfter
            || normalizedReset === Scenario.appDataResetBeforeAfter;
    }

    static normalizeAppDataReset(appDataReset: string | null | undefined): string {
        if (!appDataReset || appDataReset.trim() === '') {
            return Scenario.appDataResetNone;
        }

        if (appDataReset.toLowerCase() === Scenario.appDataResetBefore.toLowerCase()) {
            return Scenario.appDataResetBefore;
        }
        if (appDataReset.toLowerCase() === Scenario.appDataResetAfter.toLowerCase()) {
            return Scenario.appDataResetAfter;
        }
        if (appDataReset.toLowerCase() === Scenario.appDataResetBeforeAfter.toLowerCase()) {
            return Scenario.appDataResetBeforeAfter;
        }

        return Scenario.appDataResetNone;
    }

    // Returns true when this scenario declares the CLIENT execution tier — its client re-merge lane is scored
    // on a client-vs-client verdict rather than byte equality with the server render. See PLAN_HANDLER_FIX §16.
    isClientTier(): boolean {
        return (this.tier || '').toLowerCase() === Scenario.tierClient.toLowerCase();
    }

    static normalizeTier(tier: string | null | undefined): string {
        if (!tier || tier.trim() === '') {
            return Scenario.tierServer;
        }
        if (tier.toLowerCase() === Scenario.tierClient.toLowerCase()) {
            return Scenario.tierClient;
        }
        return Scenario.tierServer;
    }

    toString(): string {
        if (this.configName) {
            return `${this.appSite}:${this.appView}:${this.configName}`;
        }
        return `${this.appSite}:${this.appView}`;
    }
}

export class AppFileResult {
    appFile: string;
    validatedAppView: string;

    constructor(appFile: string, validatedAppView: string) {
        this.appFile = appFile;
        this.validatedAppView = validatedAppView;
    }
}

/**
 * Well-known artifact kinds for the format-blind source artifact accessors (getArtifactNames/getArtifactBytes).
 */
export const ArtifactKind = {
    Html: 'Html',
    DataJson: 'DataJson',
    HtmlJson: 'HtmlJson',
    Preprocessed: 'Preprocessed',
    Metadata: 'Metadata',
    GlobalData: 'GlobalData',
    GlobalViewData: 'GlobalViewData',
    DynamicDataConfig: 'DynamicDataConfig',
    DynamicViewConfig: 'DynamicViewConfig',
    DynamicActionConfig: 'DynamicActionConfig',
    DynamicDataParams: 'DynamicDataParams',
    DynamicViewParams: 'DynamicViewParams',
    DynamicActionParams: 'DynamicActionParams'
} as const;

export abstract class IConfig {
    // #region Loading Related

    getAppSites(): Set<string> {
        throw new Error('Method not implemented');
    }

    getAppViews(appSite: string): string[] {
        throw new Error('Method not implemented');
    }

    getAppSiteFolders(): string[] {
        throw new Error('Method not implemented');
    }

    getAppFile(appSite: string, appView: string): AppFileResult {
        throw new Error('Method not implemented');
    }

    // #endregion

    // #region Engine Loading Configuration

    getSearchAppSitesForAppSite(appSite: string): string {
        throw new Error('Method not implemented');
    }

    get defaultAppFile(): string {
        throw new Error('Property not implemented');
    }

    get defaultAppViewPrefix(): string {
        throw new Error('Property not implemented');
    }

    get defaultReplaceHtml(): string {
        throw new Error('Property not implemented');
    }

    get defaultReplaceTemplateName(): string {
        throw new Error('Property not implemented');
    }

    get defaultSkipHtml(): string {
        throw new Error('Property not implemented');
    }

    get defaultSkipJson(): string {
        throw new Error('Property not implemented');
    }

    get defaultSkipTemplateName(): string {
        throw new Error('Property not implemented');
    }

    // #endregion

    // #region Engine Loading Related

    getHtmlContent(appSite: string, fileName: string): string | null {
        throw new Error('Method not implemented');
    }

    getDataJson(appSite: string, fileName: string): JsonObject | null {
        return null;
    }

    getAllContentNames(appSite: string): string[] {
        throw new Error('Method not implemented');
    }

    getRelativeInfo(appSite: string, fileName: string): string | null {
        throw new Error('Method not implemented');
    }

    getAppSiteFolder(appSite: string): string | null {
        throw new Error('Method not implemented');
    }

    // #endregion

    // #region Engine Configuration

    get maxArrayOperationItems(): number {
        throw new Error('Property not implemented');
    }

    get maxRecursionDepth(): number {
        throw new Error('Property not implemented');
    }

    get maxMergeIterations(): number {
        throw new Error('Property not implemented');
    }

    get dynamicDataUseResolvedAppSite(): boolean {
        throw new Error('Property not implemented');
    }

    get globalDataMergeFromFallback(): boolean {
        throw new Error('Property not implemented');
    }

    get globalDataMergeOverwrite(): boolean {
        throw new Error('Property not implemented');
    }

    /**
     * The delivered DynamicClientData RAW island map routed from the SERVER merge
     * (MergeResult.dynamicClientData) into the CLIENT re-merge (clientMode) — the client tier reads its raw
     * island from here (the server data handler that ConfigClient does not ship). Default null: a File/OCI
     * source produces the raw fresh; only the ConfigClient console round-trip carries the delivered map.
     */
    get dynamicClientData(): JsonObject | null {
        return null;
    }

    get replaceHandlerReturn(): boolean {
        throw new Error('Property not implemented');
    }


    getRootJson(fileName: string): JsonObject {
        throw new Error('Method not implemented');
    }

    // #endregion

    // #region Source Artifact Access (format-blind)

    getArtifactNames(appSite: string, kind: string): string[] {
        throw new Error('Method not implemented');
    }

    getArtifactBytes(appSite: string, name: string, kind: string): Uint8Array | null {
        throw new Error('Method not implemented');
    }

    /**
     * True when this source serves PRE-PARSED content (ConfigClient, ConfigOCI) rather than raw
     * files (ConfigFile). An artifact source is expected to supply every artifact the loader asks
     * for, so a null getArtifactBytes there is a hydration/packaging DEFECT — the loader must fail,
     * not quietly rebuild the value from raw content. Rebuilding would hide the miss and pay the
     * full parse cost the artifact exists to avoid.
     */
    get isArtifactSource(): boolean {
        throw new Error('Property not implemented');
    }

    /**
     * Performs every source read the given AppSites (and their SearchAppSites) need and returns the
     * PreparedContent that answers the loader's content reads. This is the loader's "source" phase: a
     * filesystem source reads its files here (read mode), while a source that already holds its content
     * in memory (ConfigClient, ConfigOCI) answers through itself (resident mode). The config itself is
     * never copied or mutated. Reads on every call; never memoized. Every source implements it.
     */
    abstract prepare(appSites: string[]): PreparedContent;

    // #endregion

    // #region Folder-Level Data Support

    getFolderDataJson(appSite: string, fileName: string): JsonObject | null {
        return null;
    }

    // #endregion
}
